import { useContext, useEffect, useMemo, useRef } from "react"

import { getWindow } from "shared/lib/getWindow"
import { stopPropagation } from "shared/lib/stopPropagation"
import {
  confirmChildPosition,
  findAncestors,
  findChild
} from "shared/lib/treeUtils"
import { useEvent } from "shared/lib/useEvent"
import { useMergedRefs } from "shared/lib/useMergedRefs"
import { useNextRenderCycle } from "shared/lib/useNextRenderCycle"
import {
  CreateProjectChildEventPayload,
  MOVE_PROJECT_CHILD,
  MoveProjectChildEventPayload
} from "shared/types/AppEvent"
import { Floor } from "shared/types/Floor"
import {
  FloorTypename,
  ProductPositionTypename,
  RoomTypename,
  isProjectFloor,
  isProjectProductPosition,
  isProjectRoom
} from "shared/types/Project"
import { CREATE_PROJECT_CHILD } from "shared/types/ProjectMutations"
import { TableAPI } from "shared/ui/table/types"

import { useAppEventSubscription } from "app/useAppEventSubscription"

import {
  UNASSIGNED_PRODUCTS_TAB_ID,
  UNASSIGNED_ROOMS_TAB_ID
} from "entities/project-children/lib/projectTabIDs"
import { usePreventWindowScroll } from "features/scroll-management/model/usePreventWindowScroll"

import { ProjectPageProps } from "../ProjectPage"
import {
  ProjectChatContext,
  ProjectChatContextType
} from "../chat-context/ProjectChatContext"
import { useProjectTabState } from "./useProjectTabState"

interface PendingScrollPayload {
  id: string
  type: string
  parentID: string
  parentType: string
  projectID: string
  position?: number
  openDetailsView?: boolean
}

export function useProjectPageViewModel(args: ProjectPageProps) {
  const {
    projectModel,
    pendingProjectChildren,
    onSelect,
    onFloorSort,
    onTabSelect
  } = args
  const internalTableAPIRef = useRef<TableAPI>(null)
  const tableAPIRef = useMergedRefs(args.tableAPIRef, internalTableAPIRef)
  const pendingScrollRef = useRef<PendingScrollPayload | null>(null)
  const scheduleFn = useNextRenderCycle()

  const handleTabSelect = useEvent(
    (floorID: string | null, options?: { keepSelection?: boolean }) => {
      onTabSelect?.(floorID, options)
      getWindow()?.scrollTo({ top: 0, behavior: "instant" })
    }
  )

  const determineTargetTab = useEvent(
    (
      parentID: string,
      parentType: string,
      childID: string,
      childType: string
    ) => {
      if (parentType === FloorTypename) {
        return parentID
      }
      if (childType === FloorTypename) {
        return childID
      }
      const ancestors = findAncestors(
        childID,
        projectModel?.core.data.project?.data?.children,
        true
      )
      const floorAncestor = ancestors?.find(ancestor =>
        isProjectFloor(ancestor)
      )
      if (floorAncestor) {
        return floorAncestor.id
      }
      const roomAncestor = ancestors?.find(ancestor => isProjectRoom(ancestor))
      if (roomAncestor) {
        return UNASSIGNED_ROOMS_TAB_ID
      }

      return UNASSIGNED_PRODUCTS_TAB_ID
    }
  )

  // Scroll to element and select it
  const scrollToElement = useEvent(
    (id: string, options?: { openDetailsView?: boolean }) => {
      onSelect?.([id], {
        preventUnselectOnReselect: true,
        openDetailsView: options?.openDetailsView ?? false
      })
      internalTableAPIRef.current?.scrollToElement(id)
      internalTableAPIRef.current?.setFocusableElement(id)
    }
  )

  const savePendingScroll = useEvent(
    (
      payload: CreateProjectChildEventPayload | MoveProjectChildEventPayload,
      options?: { openDetailsView?: boolean }
    ) => {
      if (projectModel?.core.data.project?.data?.id === payload.projectID) {
        pendingScrollRef.current = {
          id: payload.id,
          type: payload.type,
          parentID: payload.parentID,
          parentType: payload.parentType,
          projectID: payload.projectID,
          position: "position" in payload ? payload.position : undefined,
          openDetailsView: options?.openDetailsView
        }
      }
    }
  )

  // A newly created child opens its detail view so it can be edited right away.
  const handleCreateProjectChild = useEvent(
    (payload: CreateProjectChildEventPayload) => {
      savePendingScroll(payload, { openDetailsView: true })
    }
  )

  const {
    show: showOffcanvas,
    hide: hideOffcanvas,
    isVisible: isOffcanvasVisible
  } = useContext(ProjectChatContext)

  usePreventWindowScroll({
    id: "project-offcanvas",
    isScrollDisabled: isOffcanvasVisible
  })

  const chatContext = useMemo<ProjectChatContextType>(
    () => ({
      show: showOffcanvas,
      hide: hideOffcanvas,
      isVisible: isOffcanvasVisible
    }),
    [isOffcanvasVisible, hideOffcanvas, showOffcanvas]
  )

  const { floors, unassignedRoomsCount, unassignedProductsCount, activeTabID } =
    useProjectTabState({
      projectModel,
      requestedTabID: args.activeTabID
    })

  const visibleChildren = useMemo(() => {
    if (activeTabID === UNASSIGNED_ROOMS_TAB_ID) {
      return (
        projectModel?.core.data.project?.data?.children?.filter(child =>
          isProjectRoom(child)
        ) ?? []
      )
    }
    if (activeTabID === UNASSIGNED_PRODUCTS_TAB_ID) {
      return (
        projectModel?.core.data.project?.data?.children?.filter(child =>
          isProjectProductPosition(child)
        ) ?? []
      )
    }
    if (activeTabID) {
      return (
        (
          projectModel?.core.data.project?.data?.children?.find(
            child => child.id === activeTabID && isProjectFloor(child)
          ) as Floor | undefined
        )?.children ?? []
      )
    }
    return (
      projectModel?.core.data.project?.data?.children?.filter(
        child => !isProjectFloor(child)
      ) ?? []
    )
  }, [activeTabID, projectModel?.core.data.project?.data?.children])

  const showClarificationRequestColumn = useMemo(() => {
    type ItemWithCR = {
      hasClarificationRequest?: boolean | null
      children?: ItemWithCR[]
    }
    const check = (items: ItemWithCR[]): boolean =>
      items.some(
        item =>
          !!item.hasClarificationRequest ||
          (item.children ? check(item.children) : false)
      )
    return check(visibleChildren as unknown as ItemWithCR[])
  }, [visibleChildren])

  useEffect(() => {
    const allChildren = projectModel?.core.data.project?.data?.children
    const pending = pendingScrollRef.current

    if (!pending) {
      return
    }
    if (pending.projectID !== projectModel?.core.data.project?.data?.id) {
      return
    }
    if (!findChild(pending.id, allChildren)) {
      return
    }
    if (pending.position !== undefined) {
      const isPositionConfirmed = confirmChildPosition({
        id: pending.id,
        children: allChildren,
        expectedParentID:
          pending.parentID === pending.projectID ? null : pending.parentID,
        expectedPositon: pending.position
      })
      if (!isPositionConfirmed) {
        return
      }
    }

    const targetTab = determineTargetTab(
      pending.parentID,
      pending.parentType,
      pending.id,
      pending.type
    )

    if (targetTab && targetTab !== activeTabID && handleTabSelect) {
      handleTabSelect(targetTab, { keepSelection: true })
    }

    if (
      pending.type === RoomTypename ||
      pending.type === ProductPositionTypename
    ) {
      scheduleFn(() =>
        scrollToElement(pending.id, {
          openDetailsView: pending.openDetailsView
        })
      )
    }

    pendingScrollRef.current = null
  }, [projectModel?.core.data.project?.data?.children, activeTabID])

  useAppEventSubscription({
    eventType: CREATE_PROJECT_CHILD,
    handler: handleCreateProjectChild
  })

  useAppEventSubscription({
    eventType: MOVE_PROJECT_CHILD,
    handler: savePendingScroll
  })

  const onSelectFloor = useEvent(
    (floorID: string, event?: React.MouseEvent) => {
      stopPropagation(event)
      handleTabSelect(floorID)
      if (onSelect) {
        onSelect([floorID], {
          preventUnselectOnReselect: true,
          openDetailsView: true
        })
      }
    }
  )

  const isTableLoading = useMemo(() => {
    return (
      !!projectModel?.core.data.project?.isLoading ||
      !!pendingProjectChildren?.includes(activeTabID ?? "")
    )
  }, [
    projectModel?.core.data.project?.isLoading,
    pendingProjectChildren,
    activeTabID
  ])

  return {
    ...args,
    isTableLoading,
    activeTabID,
    floors,
    visibleChildren,
    showClarificationRequestColumn,
    unassignedRoomsCount,
    unassignedProductsCount,
    hideChat: false,
    chatContext,
    tableAPIRef,
    onTabSelect: handleTabSelect,
    onSelectFloor,
    onFloorSort
  }
}
